import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import {
  Loader2, Search, Trash2, Landmark, CheckCircle2,
} from "lucide-react";

export const OPPONENT_PARTIES = ["Democratic", "Republican", "Independent", "Libertarian", "Green", "Other"];

/**
 * Editable opponent row with a debounced FEC-ID auto-complete on the name field.
 * Once a FEC candidate is picked, the row is border-tinted emerald and shows a
 * badge — PAC spend alerts fire on that opponent's Schedule E rows.
 *
 * Props:
 *  - opp: { name, party, fec_candidate_id }
 *  - index: number (for stable test IDs)
 *  - onChange(patch): partial updater
 *  - onRemove(): remove this opponent from the parent list
 */
export function OpponentRow({ opp, index, onChange, onRemove }) {
  const [query, setQuery] = useState(opp.name || "");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const runFetch = async (q) => {
    if (!q || q.trim().length < 3) { setResults(null); return; }
    setLoading(true);
    try {
      const r = await api.get("/political-intelligence/fec-search", { params: { q: q.trim() } });
      setResults(r.data || []);
      setOpen(true);
    } catch {
      setResults([]);
      setOpen(true);
    } finally { setLoading(false); }
  };

  const onNameChange = (val) => {
    setQuery(val);
    onChange({ name: val, fec_candidate_id: null });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runFetch(val), 300);
  };

  const pick = (row) => {
    setQuery(row.display_name);
    setResults(null);
    setOpen(false);
    onChange({
      name: row.display_name,
      party: row.party || "Other",
      fec_candidate_id: row.fec_candidate_id,
    });
  };

  const clearFec = () => onChange({ fec_candidate_id: null });

  return (
    <div ref={rootRef} className="relative" data-testid={`opponent-row-${index}`}>
      <div className="flex gap-2 items-center">
        <div className="flex-1 relative">
          <input value={query} onChange={(e) => onNameChange(e.target.value)}
            onFocus={() => { if ((results || []).length > 0) setOpen(true); }}
            placeholder="Opponent name — type 3+ chars to auto-match FEC" data-testid={`opponent-name-input-${index}`}
            className={`w-full bg-slate-900/80 border rounded-md px-3 py-2 pr-10 text-sm text-white focus:outline-none placeholder-slate-500 ${opp.fec_candidate_id ? "border-emerald-400/50 focus:border-emerald-400" : "border-white/15 focus:border-cyan-400/60"}`} />
          {opp.fec_candidate_id ? (
            <button onClick={clearFec} data-testid={`opponent-fec-clear-${index}`}
              title={`Linked FEC ${opp.fec_candidate_id} — click to unlink`}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-400 hover:text-rose-400">
              <CheckCircle2 className="w-4 h-4" />
            </button>
          ) : loading ? (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-cyan-300" />
          ) : (
            <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" />
          )}
        </div>
        <select value={opp.party || "Other"} onChange={(e) => onChange({ party: e.target.value })}
          data-testid={`opponent-party-select-${index}`}
          className="bg-slate-900/80 border border-white/15 rounded-md px-2 py-2 text-sm text-white focus:outline-none">
          {OPPONENT_PARTIES.map(p => <option key={p}>{p}</option>)}
        </select>
        <button onClick={onRemove} data-testid={`opponent-remove-${index}`} className="text-slate-500 hover:text-rose-300 p-1">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {opp.fec_candidate_id && (
        <div className="text-[10px] text-emerald-300 mt-1 pl-1" data-testid={`opponent-fec-badge-${index}`}>
          FEC {opp.fec_candidate_id} · PAC alerts will fire for this opponent&apos;s Schedule E spend
        </div>
      )}

      {open && results && (
        <div className="absolute z-20 left-0 right-24 top-full mt-1 max-h-64 overflow-y-auto rounded-lg border border-white/10 bg-slate-950 shadow-2xl divide-y divide-white/5"
             data-testid={`opponent-fec-dropdown-${index}`}>
          {results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">No FEC match — keep the name as-is to track without alerts.</div>
          ) : (
            results.slice(0, 8).map(r => (
              <button key={r.fec_candidate_id} onClick={() => pick(r)}
                data-testid={`opponent-fec-pick-${index}-${r.fec_candidate_id}`}
                className="w-full text-left px-3 py-2 hover:bg-cyan-400/10 transition-colors">
                <div className="text-sm text-white flex items-center gap-2">
                  <Landmark className="w-3 h-3 text-cyan-300 shrink-0" />
                  {r.display_name}
                </div>
                <div className="text-[10px] text-slate-500 pl-5">
                  {r.party} · {r.office_full} · {r.state}{r.district && r.district !== "00" ? `-${r.district}` : ""} · FEC {r.fec_candidate_id}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
